"use client";

import type * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { SmoothCursor, type SmoothCursorProps } from "./smooth-cursor";

type CursorAction = "click" | "hover";
export type CursorMessageVariant = "default" | "speech";

export type CursorMessageProps = {
	title: string;
	action: CursorAction;
	duration?: number;
	triggerOnce?: boolean;
} & { children: React.ReactNode };

type CursorState = {
	title: string;
	variant: CursorMessageVariant;
	cursorClasses: string;
	duration?: number;
	visible: boolean;
} & SmoothCursorProps;

const DEFAULT_CURSOR_CLASSES =
	"px-3 py-1 rounded-lg text-sm font-extrabold shadow-sm backdrop-blur-md";

function variantClasses(variant: CursorMessageVariant): string {
	switch (variant) {
		case "speech":
			return "bg-white/90 text-slate-900";
		default:
			return "bg-indigo-500/50 text-white";
	}
}

let setCursorStateGlobal: React.Dispatch<
	React.SetStateAction<CursorState>
> | null = null;
let hideTimer: ReturnType<typeof setTimeout> | null = null;
let defaultPortalConfig: Pick<
	CursorState,
	| "variant"
	| "cursorClasses"
	| "springConfig"
	| "opacity"
	| "rotate"
	| "offsetX"
	| "offsetY"
	| "alignCenter"
> | null = null;

function showCursorMessage(next: CursorState) {
	if (!setCursorStateGlobal) {
		return;
	}
	if (hideTimer) {
		clearTimeout(hideTimer);
		hideTimer = null;
	}

	setCursorStateGlobal((prev) => ({
		...prev,
		...next,
		visible: true,
	}));

	if (next.duration && next.duration > 0) {
		hideTimer = setTimeout(() => {
			setCursorStateGlobal?.((prev) => ({ ...prev, visible: false }));
		}, next.duration);
	}
}

function CursorMessagePortal() {
	const [state, setState] = useState<CursorState>({
		title: "",
		variant: defaultPortalConfig?.variant ?? "default",
		cursorClasses: DEFAULT_CURSOR_CLASSES,
		duration: undefined,
		visible: false,
		springConfig: defaultPortalConfig?.springConfig ?? {
			damping: 150,
			stiffness: 500,
			mass: 1,
			restDelta: 0.001,
		},
		opacity: defaultPortalConfig?.opacity ?? 1,
		rotate: defaultPortalConfig?.rotate ?? false,
		offsetX: defaultPortalConfig?.offsetX ?? 25,
		offsetY: defaultPortalConfig?.offsetY ?? 25,
		cursor: null,
		alignCenter: defaultPortalConfig?.alignCenter ?? false,
	});

	useEffect(() => {
		setCursorStateGlobal = setState;
		return () => {
			if (setCursorStateGlobal === setState) {
				setCursorStateGlobal = null;
			}
		};
	}, []);

	const cursorNode = useMemo(() => {
		const isSpeech = state.variant === "speech";
		return (
			<div
				className={`${state.cursorClasses} ${variantClasses(state.variant)} relative transition-all duration-200 ease-out`}
				style={{
					opacity: state.visible ? 1 : 0,
					transform: state.visible ? "scale(1)" : "scale(0.95)",
				}}
			>
				{isSpeech ? (
					<span
						aria-hidden
						className="absolute -bottom-1.5 left-3 size-3 rotate-45 rounded-sm bg-white/90 shadow-sm"
					/>
				) : null}
				{state.title}
			</div>
		);
	}, [state.cursorClasses, state.title, state.visible, state.variant]);

	return (
		<SmoothCursor
			cursor={cursorNode}
			springConfig={state.springConfig}
			opacity={state.opacity}
			rotate={state.rotate}
			offsetX={state.offsetX}
			offsetY={state.offsetY}
			alignCenter={false}
			showIntentBadges={false}
		/>
	);
}

/**
 * Mounts the second cursor's portal once, globally.
 * Render this once near the app root if you plan to use `CursorMessage`.
 */
export function CursorMessagePortalMount() {
	// Default appearance/physics for the message cursor, so `CursorMessage` wrappers
	// only need to provide dynamic trigger values (title/action/duration/once).
	useEffect(() => {
		defaultPortalConfig = {
			variant: "speech",
			cursorClasses: DEFAULT_CURSOR_CLASSES,
			springConfig: {
				damping: 150,
				stiffness: 500,
				mass: 1,
				restDelta: 0.001,
			},
			opacity: 1,
			rotate: false,
			offsetX: 25,
			offsetY: 25,
			alignCenter: false,
		};
		return () => {
			defaultPortalConfig = null;
		};
	}, []);
	const [shouldRender, setShouldRender] = useState(false);
	useEffect(() => {
		// mount once on client
		setShouldRender(true);
	}, []);
	return shouldRender ? <CursorMessagePortal /> : null;
}

export function CursorMessage({
	children,
	title,
	action,
	duration,
	triggerOnce = false,
}: CursorMessageProps) {
	const hasTriggeredRef = useRef(false);

	const trigger = () => {
		if (triggerOnce && hasTriggeredRef.current) {
			return;
		}
		hasTriggeredRef.current = true;
		const defaults = defaultPortalConfig;
		showCursorMessage({
			title,
			duration,
			variant: defaults?.variant ?? "default",
			cursorClasses: defaults?.cursorClasses ?? DEFAULT_CURSOR_CLASSES,
			springConfig: defaults?.springConfig ?? {
				damping: 150,
				stiffness: 500,
				mass: 1,
				restDelta: 0.001,
			},
			opacity: defaults?.opacity ?? 1,
			rotate: defaults?.rotate ?? false,
			offsetX: defaults?.offsetX ?? 25,
			offsetY: defaults?.offsetY ?? 25,
			alignCenter: defaults?.alignCenter ?? false,
			cursor: null,
			visible: true,
		});
	};

	const eventProps =
		action === "click"
			? { onClick: trigger }
			: {
					onMouseEnter: trigger,
				};

	return <span {...eventProps}>{children}</span>;
}
